import React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DateTime } from 'luxon';
import isEmail from 'validator/lib/isEmail';

function UserDialog(props) {
    const [email, setEmail] = React.useState(null);
    const [emailError, setEmailError] = React.useState(false);
    const [userType, setUserType] = React.useState(null);
    const [savingUser, setSavingUser] = React.useState(false);

    const closeDialog = () => {
        setEmailError(false);
        props.showUserDialog(false, null)();
    };

    const setEmailHelper = e => {
        setEmail(e.target.value);
    };

    const setUserTypeHelper = e => {
        setUserType(e.target.value);
    };

    const saveUser = () => {
        if (email == null || !isEmail(email)) {
            setEmailError(true);
            return;
        }

        setEmailError(false);
        setSavingUser(true);
        
        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/users/" + email, {
            headers: {
                Authorization: "Bearer " + props.token,
                "Content-Type": "application/json"
            },
            method: "PUT",
            body: JSON.stringify({
                type: userType
            })
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(result => {
            props.notify(null, "success", 'Saved user: ' + result.email);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        }).finally(() => {
            props.getUsers();
            props.showUserDialog(false, null)();
            setSavingUser(false);
        });
    };

    React.useEffect(() => {
        setEmail(props.user == null ? null : props.user.email);
        setUserType(props.user == null ? "candidate" : props.user.type);
    }, [props.open]);

    return (
        <Dialog open={props.open} maxWidth="md" fullWidth>
            <DialogTitle>{props.user == null ? "Add a user": "Edit user"}</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <DialogContentText>
                            Enter the user's details
                        </DialogContentText>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField disabled={!(props.user == null)} required error={emailError} id="email" type="email" label="E-mail address" defaultValue={props.user == null ? "" : props.user.email} fullWidth onChange={setEmailHelper} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl required fullWidth>
                            <InputLabel id="type-label">Type</InputLabel>
                            <Select labelId="type-label" id="type" label="Type" defaultValue={props.user == null ? "candidate" : props.user.type} onChange={setUserTypeHelper}>
                                <MenuItem value="candidate">Candidate</MenuItem>
                                <MenuItem value="racker">Racker</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={saveUser}>
                        Save
                        <Box sx={{ display: savingUser ? "contents" : "none" }}>
                            &nbsp;<CircularProgress size="1rem" color="inherit" />
                        </Box>
                    </Button>
                    <Button variant="contained" color="error" onClick={closeDialog}>Cancel</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    )
}

function AssignLabDialog(props) {
    const [labs, setLabs] = React.useState([]);
    const [selectedLab, setSelectedLab] = React.useState(null);

    const getLabs = React.useCallback(() => {
        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/labs", {
            headers: {
                Authorization: "Bearer " + props.token
            }
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(labs => {
            setLabs(labs);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        });
    }, [props.token]);
    
    React.useEffect(() => getLabs(), [getLabs]);

    const assignLab = React.useCallback((email, lab) => () => {
        setSelectedLab(lab);

        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/users/" + email + "/assignedLabs", {
            headers: {
                Authorization: "Bearer " + props.token,
                "Content-Type": "application/json"
            },
            method: "POST",
            body: JSON.stringify({
                labName: lab
            })
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(result => {
            props.notify(null, "success", 'Assigned lab: ' + result.id);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        }).finally(() => {
            props.setAssignLabDialogEmail(null);
            props.getUsers();
            setSelectedLab(null);
        });
    }, [props.setAssignLabDialogEmail, props.getUsers, props.notify]);

    return (
        <Dialog open={props.email == null ? false : true} maxWidth="md" fullWidth>
            <DialogTitle>Assign a lab</DialogTitle>
            <DialogContent>
                <DialogContentText>Select a lab to assign to {props.email}</DialogContentText>
                <List>
                    {labs.map(lab => (
                        <ListItem key={lab} disablePadding>
                            <ListItemButton onClick={assignLab(props.email, lab)}>
                                <ListItemText primary={lab} />
                                <CircularProgress sx={{ display: selectedLab == lab ? "block" : "none" }} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" color="error" onClick={props.showAssignLab(null)}>Cancel</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    )
}

function DeleteUserDialog(props) {
    const [deletingUser, setDeletingUser] = React.useState(false);

    const deleteUser = email => () => {
        setDeletingUser(true);

        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/users/" + email, {
            headers: {
                Authorization: "Bearer " + props.token,
                "Content-Type": "application/json"
            },
            method: "DELETE"
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(result => {
            props.notify(null, "success", 'Deleted user: ' + email);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        }).finally(() => {
            props.getUsers();
            setDeletingUser(false);
            closeDeleteDialog();
        });
    };

    const closeDeleteDialog = () => {
        props.setDeleteDialogUser(null);
    };

    return (
        <Dialog open={props.email != null} maxWidth="sm" fullWidth>
            <DialogTitle>Delete user</DialogTitle>
            <DialogContent>
                <DialogContentText>Delete {props.email}?</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" color="error" onClick={deleteUser(props.email)}>
                        Yes
                        <Box sx={{ display: deletingUser ? "contents" : "none" }}>
                            &nbsp;<CircularProgress size="1rem" color="inherit" />
                        </Box>    
                    </Button>
                    <Button variant="contained" onClick={closeDeleteDialog}>No</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    )
}

function Users(props) {
    const zeroTime = DateTime.fromISO("0001-01-01T00:00:00Z");
    const [users, setUsers] = React.useState([]);
    const [assignLabDialogEmail, setAssignLabDialogEmail] = React.useState(null);
    const [userDialogOpen, setUserDialogOpen] = React.useState(false);
    const [userDialogUser, setUserDialogUser] = React.useState(null);
    const [deleteDialogUser, setDeleteDialogUser] = React.useState(null);

    const columns = [
        { field: "email", headerName: "E-mail", flex: 1 },
        { field: "type", headerName: "Type", flex: 1, type: "singleSelect", valueOptions: ["admin", "candidate", "racker"]},
        { field: "assignedLabs", headerName: "Assigned labs", flex: 1, renderCell: (params) => (
            <Grid container>
                {params.value.map(value => (
                    <Grid item key={value.id}>
                        <b>{value.labName}</b>&nbsp;
                        {DateTime.fromISO(value.launchTime).equals(zeroTime) ? "Pending" : (DateTime.fromISO(value.scoreTime).equals(zeroTime) ? "Launched" : "Scored " + value.score.percentage * 100 + '%')}&nbsp;
                        <GridActionsCellItem icon={<RemoveCircleIcon />} label="Unassign lab" onClick={unassignLab(params.id, value.id)} />
                    </Grid>
                ))}
            </Grid>
        ) },
        { field: "actions", type: "actions", getActions: (params) => [
            <GridActionsCellItem icon={<AssignmentIcon />} label="Assign lab" onClick={showAssignLab(params.id)} />,
            <GridActionsCellItem icon={<EditIcon />} label="Edit user" onClick={showUserDialog(true, params.row)} />,
            <GridActionsCellItem icon={<DeleteIcon />} label="Delete user" onClick={showDeleteUserDialog(params.id)} />
        ]}
    ]

    const getUsers = React.useCallback(() => {
        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/users?full", {
            headers: {
                Authorization: "Bearer " + props.token
            }
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(users => {
            setUsers(users);
        })
        .catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        });
    }, [props.token]);

    React.useEffect(() => getUsers(), [getUsers]);

    const showUserDialog = (open, user) => () => {
        setUserDialogUser(user);
        setUserDialogOpen(open);
    };

    const showAssignLab = React.useCallback(email => () => {
        setAssignLabDialogEmail(email);
    }, []);

    const unassignLab = React.useCallback((email, id) => () => {
        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/users/" + email + "/assignedLabs/" + id, {
            headers: {
                Authorization: "Bearer " + props.token
            },
            method: "DELETE"
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(result => {
            props.notify(null, "success", 'Unassigned lab: ' + id);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        }).finally(() => {
            getUsers();
        });
    }, [getUsers]);

    const showDeleteUserDialog = email => () => {
        setDeleteDialogUser(email);
    };

    return (
        <Container>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <DataGrid autoHeight columns={columns} rows={users} getRowId={row => row.email} getRowHeight={() => "auto" } />
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                        <Button variant="contained" onClick={showUserDialog(true, null)}>
                            Add user
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <UserDialog open={userDialogOpen} user={userDialogUser} showUserDialog={showUserDialog} getUsers={getUsers} token={props.token} notify={props.notify} />
            <AssignLabDialog email={assignLabDialogEmail} showAssignLab={showAssignLab} setAssignLabDialogEmail={setAssignLabDialogEmail} token={props.token} getUsers={getUsers} notify={props.notify} />
            <DeleteUserDialog email={deleteDialogUser} setDeleteDialogUser={setDeleteDialogUser} token={props.token} getUsers={getUsers} notify={props.notify} />
        </Container>
    )
}

export default Users;